// aluConstraints.js — validacion pura de limites de fabrica (Doorwin).
// Migraciones 061 (columnas en quote_lines_alu) y 062 (tabla alu_constraints).
//
// ARCHIVO ESPEJO: existe copia IDENTICA en frontend/src/utils/aluConstraints.js
// Se actualizan JUNTOS, misma doctrina que aluElevation.js.
//
// D.20: panel_count llega YA RESUELTO como argumento. Este archivo no conoce
// el vocabulario de panel_config; quien llama lo calcula (front: ALU.panelCountFrom
// o bifold_panels; back: parser propio en quotesController).
//
// D.16: max_weight_lb NO se valida (no hay modelo de peso para aluminio).
// Se devuelve en las notas para que el vendedor lo vea.

const SQIN_PER_SQFT = 144;

// Una fila aplica si cada discriminador o es NULL (comodin) o coincide exacto.
function rowMatches(row, line) {
  if (!row.active) return false;
  if (row.series && row.series !== line.series) return false;
  if (row.product_type && row.product_type !== line.product_type) return false;
  if (row.frame_material && row.frame_material !== line.frame_material) return false;
  if (row.opens && row.opens !== line.opens) return false;
  return true;
}

// Dimensiones efectivas segun el alcance de la fila.
// panel  -> ancho repartido entre paneles (el alto NO se reparte)
// unit   -> la unidad tal cual se capturo
// system -> el conjunto completo, igual que unit
function effectiveDims(scope, line) {
  const w = Number(line.width_in);
  const h = Number(line.height_in);
  if (!(w > 0) || !(h > 0)) return null;
  if (scope === 'panel') {
    const n = Number(line.panel_count);
    if (!(n > 0)) return null;          // sin conteo no se puede validar -> unverified
    return { w: w / n, h, per: 'panel' };
  }
  return { w, h, per: scope === 'system' ? 'system' : 'unit' };
}

const r2 = (n) => Math.round(n * 100) / 100;

function checkRow(row, line) {
  const d = effectiveDims(row.scope, line);
  if (!d) return { unverified: true, row };

  const area = r2((d.w * d.h) / SQIN_PER_SQFT);
  const out = [];
  const push = (kind, actual, limit, unit) =>
    out.push({ kind, scope: row.scope, per: d.per, actual: r2(actual),
               limit: Number(limit), unit, modular: row.modular,
               severity: row.severity, note: row.note, series: row.series });

  if (row.max_w_in != null && d.w > Number(row.max_w_in)) push('max_w', d.w, row.max_w_in, 'in');
  if (row.max_h_in != null && d.h > Number(row.max_h_in)) push('max_h', d.h, row.max_h_in, 'in');
  if (row.min_w_in != null && d.w < Number(row.min_w_in)) push('min_w', d.w, row.min_w_in, 'in');
  if (row.min_h_in != null && d.h < Number(row.min_h_in)) push('min_h', d.h, row.min_h_in, 'in');
  if (row.max_area_sqft != null && area > Number(row.max_area_sqft))
    push('max_area', area, row.max_area_sqft, 'sqft');

  return { unverified: false, violations: out, row };
}

// validateAluLine(line, constraints) -> resultado de validacion.
//
// line: { series, product_type, frame_material, opens, width_in, height_in,
//         panel_count }   (panel_count puede ser null)
// constraints: filas de alu_constraints (todas; aqui se filtran)
//
// Devuelve:
//   status      'ok' | 'modular' | 'unverified'   (nunca 'override': eso lo
//               decide un admin, no el validador)
//   blocked     true = no se puede guardar sin override
//   violations  detalle para pintar en el modal
//   limits      limites aplicables, para el hint ANTES de teclear (D.10)
function validateAluLine(line, constraints) {
  const rows = (constraints || []).filter((r) => rowMatches(r, line));

  // Limites aplicables: sirven para el hint aunque no haya medidas todavia.
  const limits = rows.map((r) => ({
    scope: r.scope, max_w_in: r.max_w_in, max_h_in: r.max_h_in,
    min_w_in: r.min_w_in, min_h_in: r.min_h_in,
    max_area_sqft: r.max_area_sqft, max_weight_lb: r.max_weight_lb,
    modular: r.modular, confidence: r.confidence, note: r.note,
  }));

  // Sin filas = producto sin limites capturados (Custom, serie sin datos).
  if (!rows.length) {
    return { status: 'unverified', blocked: false, violations: [], limits,
             reason: 'no_constraints' };
  }

  const results = rows.map((r) => checkRow(r, line));

  // Si alguna fila de scope panel no pudo evaluarse por falta de conteo,
  // la linea NO se declara ok: se declara no verificada. Callar seria peor.
  if (results.some((r) => r.unverified)) {
    return { status: 'unverified', blocked: false, violations: [], limits,
             reason: 'missing_dims_or_panel_count' };
  }

  const violations = results.flatMap((r) => r.violations);
  if (!violations.length) return { status: 'ok', blocked: false, violations: [], limits };

  // Excederse en una fila modular no es error: significa mas modulos/paneles.
  // Pero si ademas se violo una fila NO modular, manda el bloqueo.
  const hard = violations.filter((v) => !v.modular && v.severity === 'block');
  if (hard.length) return { status: null, blocked: true, violations, limits };

  const modular = violations.some((v) => v.modular);
  return { status: modular ? 'modular' : 'ok', blocked: false, violations, limits };
}

// Texto corto para el modal y para el mensaje de error del backend.
function describeViolation(v) {
  // El alto NO se reparte entre paneles (comparten altura): decir 'por panel'
  // en max_h/min_h se lee como si se hubiera dividido. El numero es correcto.
  const splits = v.kind !== 'max_h' && v.kind !== 'min_h';
  const per = !splits ? '' : v.per === 'panel' ? ' per panel' : v.per === 'system' ? ' system' : '';
  const label = { max_w: 'Width', max_h: 'Height', min_w: 'Width', min_h: 'Height',
                  max_area: 'Area' }[v.kind] || v.kind;
  const cmp = v.kind.startsWith('min') ? 'min' : 'max';
  return `${label}${per} ${v.actual}${v.unit} - factory ${cmp} ${v.limit}${v.unit}`;
}

export { validateAluLine, describeViolation };